import { Component, DestroyRef, EventEmitter, inject, Output } from '@angular/core';
import { MemberAuthFlow } from '../../../../../models/users/enum/member-auth-flow.enum';
import { MemberAuthViewModel } from './member-auth-view-model';
import { ActivatedRoute, Router } from '@angular/router';
import { LoadingOptions } from '../../../../../models/shared/loading-options';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Routes } from '../../../../../../routes/routes';

@Component({
  selector: 'app-member-auth',
  templateUrl: './member-auth.component.html',
  styleUrls: ['./member-auth.component.scss']
})
export class MemberAuthComponent {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private viewModel = inject(MemberAuthViewModel);
  private destroyRef = inject(DestroyRef);

  @Output() loadingOpts = new EventEmitter<LoadingOptions>();

  public showBackButton$ = this.viewModel.showBackButton$;
  public authFlow$ = this.viewModel.authFlow$;

  private listenToLoadingOpts = this.viewModel.loadingOpts$
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(opts => this.loadingOpts.emit(opts));

  private listenToAuthSuccess = this.viewModel.authSuccess.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
    this.router.navigate([Routes.dashboard()]).then();
  });

  private listenToNextAuthFlow = this.viewModel.nextAuthFlow
    .notNull()
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(nextAuthFlow => {
      switch (nextAuthFlow) {
        case MemberAuthFlow.ResetPassword:
          this.router.navigate(['auth/reset-password']).then();
          break;
        case MemberAuthFlow.SignIn:
          this.router.navigate(['auth/sign-in']).then();
          break;
        case MemberAuthFlow.MemberWelcome:
          this.router.navigate(['auth/member-welcome']).then();
          break;
        case MemberAuthFlow.MemberSetup:
          this.router.navigate(['auth/member-setup']).then();
          break;
        case MemberAuthFlow.MemberEnterEmailVerification:
          this.router.navigate(['auth/member-enter-email-verification']).then();
          break;
        case MemberAuthFlow.MemberSendEmailVerification:
          this.router.navigate(['auth/member-send-email-verification']).then();
          break;
        case MemberAuthFlow.MemberMFAVerification:
          this.router.navigate(['auth/verify-mfa']).then();
          break;
      }
    });

  private listenToUrl = this.route.url.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
    next: url => {
      switch (url[url.length - 1].path) {
        case 'sign-in': {
          this.viewModel.setAuthFlow(MemberAuthFlow.SignIn);
          break;
        }
        case 'forgot-password': {
          this.viewModel.setAuthFlow(MemberAuthFlow.ForgotPassword);
          break;
        }
        case 'reset-password': {
          this.viewModel.setAuthFlow(MemberAuthFlow.ResetPassword);
          break;
        }
        case 'member-welcome': {
          this.viewModel.setAuthFlow(MemberAuthFlow.MemberWelcome);
          break;
        }
        case 'member-setup': {
          this.viewModel.setAuthFlow(MemberAuthFlow.MemberSetup);
          break;
        }
        case 'member-enter-email-verification': {
          this.viewModel.setAuthFlow(MemberAuthFlow.MemberEnterEmailVerification);
          break;
        }
        case 'member-send-email-verification': {
          this.viewModel.setAuthFlow(MemberAuthFlow.MemberSendEmailVerification);
          break;
        }
        case 'verify-mfa': {
          this.viewModel.setAuthFlow(MemberAuthFlow.MemberMFAVerification);
          break;
        }
        case 'access-restricted': {
          this.viewModel.setAuthFlow(MemberAuthFlow.MemberAccessRestricted);
          break;
        }
      }
    }
  });

  public goBack(): void {
    this.viewModel.goBack();
  }

  protected readonly MemberAuthFlow = MemberAuthFlow;
}
